/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { createSlice, type PayloadAction, type UnknownAction } from '@reduxjs/toolkit';

import type { CsrAccount } from 'sarsaparilla/auth';

import type { Account, PostLoginResponseBase } from 'shared-ui/api/account';

/**
 * Nested profile carried on a signed-in session at `auth.account.account`.
 *
 * Alias of the login-payload `Account` so the wire type stays the single
 * source of truth. Named `AuthAccount` at the session boundary so readers
 * are not coupled to the API module's export name.
 */
export type AuthAccount = Account;

/**
 * The session wrapper held at `auth.account` while somebody is signed in.
 * `account` is optional because guest sessions and the pre-hydrate render
 * may omit the nested profile.
 */
export type AuthSession = Omit<PostLoginResponseBase, 'account'> & {
    account?: Account;
};

export interface AuthSliceState {
    /**
     * `null` whenever nobody is signed in, and also on the first render of a
     * signed-in session before the account data has been hydrated. Narrow
     * before reading; do not assume it is populated.
     */
    account: AuthSession | null;
    csrAccount: CsrAccount | null;
    errorText: string;
    isGuest: boolean;
    loginError: boolean;
}

const initialState: AuthSliceState = {
    account: null,
    csrAccount: null,
    errorText: '',
    isGuest: false,
    loginError: false,
};

function applySessionWrite(state: AuthSliceState, profile: AuthSession): void {
    state.account = profile;
    state.isGuest = Boolean(profile.is_guest);
    state.loginError = false;
}

function applyLogout(state: AuthSliceState): void {
    // Sign-out is a full reset: return every field to its initial value so no
    // stale flag survives. In practice `logout()` hard-refreshes the page, which
    // rebuilds the store from scratch anyway, but the reducer must be correct on
    // its own in case that ever stops being true. `isGuest` in particular was
    // previously left set after a guest signed out.
    state.account = initialState.account;
    state.csrAccount = initialState.csrAccount;
    state.errorText = initialState.errorText;
    state.isGuest = initialState.isGuest;
    state.loginError = initialState.loginError;
}

function applyLoginFailed(state: AuthSliceState, errorText?: string): void {
    state.account = null;
    state.loginError = true;
    state.errorText = errorText ?? '';
}

function applyCsrCustomerCleared(state: AuthSliceState): void {
    state.account = null;
}

interface LoginSuccessCompatAction extends UnknownAction {
    type: 'LOGIN_SUCCESS';
    profile: AuthSession;
}

interface LoginFailedCompatAction extends UnknownAction {
    type: 'LOGIN_FAILED';
    payload?: string;
}

interface CsrLoginSuccessCompatAction extends UnknownAction {
    type: 'CSR_LOGIN_SUCCESS';
    profile: CsrAccount;
}

function isLoginSuccessAction(action: UnknownAction): action is LoginSuccessCompatAction {
    return action.type === 'LOGIN_SUCCESS';
}

function isLoginFailedAction(action: UnknownAction): action is LoginFailedCompatAction {
    return action.type === 'LOGIN_FAILED';
}

function isCsrLoginSuccessAction(
    action: UnknownAction
): action is CsrLoginSuccessCompatAction {
    return action.type === 'CSR_LOGIN_SUCCESS';
}

function isLogoutSuccessAction(action: UnknownAction): boolean {
    return action.type === 'LOGOUT_SUCCESS';
}

function isCsrClearSelectedAction(action: UnknownAction): boolean {
    return action.type === 'CSR_CLEAR_SELECTED';
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        sessionWritten(state, action: PayloadAction<AuthSession>) {
            applySessionWrite(state, action.payload);
        },
        loggedOut(state) {
            applyLogout(state);
        },
        csrSessionWritten(state, action: PayloadAction<CsrAccount>) {
            state.csrAccount = action.payload;
            state.loginError = false;
        },
        loginFailed: {
            reducer(state, action: PayloadAction<string | undefined>) {
                applyLoginFailed(state, action.payload);
            },
            prepare(errorText?: string) {
                return { payload: errorText };
            },
        },
        csrCustomerCleared(state) {
            applyCsrCustomerCleared(state);
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(isLoginSuccessAction, (state, action) => {
            applySessionWrite(state, action.profile);
        });
        builder.addMatcher(isLoginFailedAction, (state, action) => {
            applyLoginFailed(state, action.payload);
        });
        builder.addMatcher(isLogoutSuccessAction, (state) => {
            applyLogout(state);
        });
        builder.addMatcher(isCsrLoginSuccessAction, (state, action) => {
            state.csrAccount = action.profile;
            state.loginError = false;
        });
        builder.addMatcher(isCsrClearSelectedAction, (state) => {
            applyCsrCustomerCleared(state);
        });
    },
});

export const {
    sessionWritten,
    loggedOut,
    csrSessionWritten,
    loginFailed,
    csrCustomerCleared,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
